import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Advertisement } from '@/types';
import { useAuthStore } from '@store/authStore';
import { Button } from '@atoms/Button';
import { MARKETING_HERO_IMAGE } from '@/constants/marketingAssets';
import { mediaUrl } from '@/utils/mediaUrl';
import { goToSection } from '@/utils/scrollToSection';

interface HeroSectionProps {
  ads: Advertisement[];
}

export function HeroSection({ ads }: HeroSectionProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (ads.length <= 1) return;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % ads.length), 8000);
    return () => clearInterval(timer);
  }, [ads.length]);

  const hero = ads[index];
  const heroImage = hero ? mediaUrl(hero.image) : null;
  const displayImage = heroImage ?? MARKETING_HERO_IMAGE;

  const handleCta = () => {
    if (!hero) {
      navigate('/login');
      return;
    }
    const wholesalerId = hero.wholesalerId;
    if (wholesalerId) {
      if (isAuthenticated && user?.role === 'restaurant') {
        navigate(`/restaurant?tab=browse&wholesaler=${wholesalerId}`);
        return;
      }
      navigate(`/login?redirect=${encodeURIComponent(`/restaurant?tab=browse&wholesaler=${wholesalerId}`)}`);
      return;
    }
    if (hero.buttonLink?.startsWith('/')) {
      navigate(hero.buttonLink);
      return;
    }
    if (hero.buttonLink) window.location.href = hero.buttonLink;
    else navigate('/login');
  };

  return (
    <section className="section-container py-10 md:py-16 lg:py-20">
      <div className="grid-pattern relative overflow-hidden rounded-3xl border border-slate-200 bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-slate-50/80" />
        <div className="relative grid items-center gap-10 p-8 md:grid-cols-2 md:p-12 lg:p-16">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-600">B2B Toptan Gıda</p>
            {hero ? (
              <>
                <h1 className="text-display mt-4">{hero.title}</h1>
                {hero.subtitle && <p className="text-lead mt-5">{hero.subtitle}</p>}
              </>
            ) : (
              <>
                <h1 className="text-display mt-4">
                  Toptan tedarikte <span className="text-brand-600">güvenilir</span> partneriniz
                </h1>
                <p className="text-lead mt-5">
                  Restoranınız için en iyi toptancılardan hızlı sipariş, şeffaf fiyat ve profesyonel iş ortaklığı.
                </p>
              </>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={handleCta}>
                {hero?.buttonText || 'Hemen Başla'}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => goToSection(navigate, 'categories')}>
                Kategorileri Keşfet
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-card">
              <img
                src={displayImage}
                alt={hero?.title ?? 'Lezzet Yolda Toptan — B2B gıda tedarik platformu'}
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            {ads.length > 1 && (
              <div className="mt-4 flex gap-1">
                {ads.map((ad, i) => (
                  <button
                    key={ad.id}
                    type="button"
                    aria-label={`Slayt ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className="h-1 flex-1 overflow-hidden rounded-full bg-slate-200"
                  >
                    <span
                      className={`block h-full rounded-full bg-brand-500 transition-all ${i === index ? 'w-full' : 'w-0'}`}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
